import * as React from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { AppSidebar } from '@/components/app-sidebar'
import { BreadcrumbProvider, useBreadcrumb } from '@/contexts/BreadcrumbContext'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Separator } from '@/components/ui/separator'
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar'
import { ThemeToggle } from '@/components/theme-toggle'

export interface BreadcrumbOverride {
  companyName?: string
  templateName?: string
  sourceName?: string
}

export type BreadcrumbCrumb = { label: string; href?: string }

function getBreadcrumbs(
  pathname: string,
  overrides: BreadcrumbOverride = {}
): BreadcrumbCrumb[] {
  // Strip /app prefix for breadcrumb logic
  const normalizedPath = pathname.startsWith('/app') ? pathname.slice(4) || '/' : pathname
  const segments = normalizedPath.split('/').filter(Boolean)

  // Static exact matches (preserve existing behavior)
  const staticMap: Record<string, BreadcrumbCrumb[]> = {
    '/dashboard': [{ label: 'Dashboard' }],
    '/sources': [{ label: 'Sources' }],
    '/lists': [{ label: 'Lists' }],
    '/outreach': [{ label: 'Outreach' }],
    '/jobs': [{ label: 'Jobs' }],
    '/settings/organization': [
      { label: 'Settings', href: '/app/settings/organization' },
      { label: 'Organization' },
    ],
    '/settings/team': [
      { label: 'Settings', href: '/app/settings/organization' },
      { label: 'Team' },
    ],
    '/settings/members': [
      { label: 'Settings', href: '/app/settings/organization' },
      { label: 'Members' },
    ],
    '/settings/invites': [
      { label: 'Settings', href: '/app/settings/organization' },
      { label: 'Invites' },
    ],
    '/settings/join-requests': [
      { label: 'Settings', href: '/app/settings/organization' },
      { label: 'Join Requests' },
    ],
  }

  if (staticMap[normalizedPath]) {
    return staticMap[normalizedPath]
  }

  // Companies: /companies | /companies/:id | /companies/:id/outreach
  if (segments[0] === 'companies') {
    if (segments.length === 1) {
      return [{ label: 'Companies' }]
    }
    const companyId = segments[1]
    const companyLabel = overrides.companyName ?? 'Company'
    const companyHref = `/app/companies/${companyId}`

    if (segments.length === 2) {
      return [
        { label: 'Companies', href: '/app/companies' },
        { label: companyLabel, href: companyHref },
      ]
    }
    if (segments.length === 3 && segments[2] === 'outreach') {
      return [
        { label: 'Companies', href: '/app/companies' },
        { label: companyLabel, href: companyHref },
        { label: 'Outreach Email' },
      ]
    }
  }

  // Sources: /sources | /sources/:id/config
  if (segments[0] === 'sources') {
    if (segments.length === 1) {
      return [{ label: 'Sources' }]
    }
    if (segments.length === 3 && segments[2] === 'config') {
      const sourceId = segments[1]
      const sourceLabel = overrides.sourceName ?? `Source ${sourceId}`
      return [
        { label: 'Sources', href: '/app/sources' },
        { label: sourceLabel },
        { label: 'Search Configuration' },
      ]
    }
  }

  // Lists: /lists | /lists/:id
  if (segments[0] === 'lists') {
    if (segments.length === 1) {
      return [{ label: 'Lists' }]
    }
    if (segments.length === 2) {
      const runId = segments[1]
      const shortId = runId.length > 8 ? runId.slice(0, 8) : runId
      return [
        { label: 'Lists', href: '/app/lists' },
        { label: `Run ${shortId}` },
      ]
    }
  }

  // Outreach: /outreach | /outreach/templates | /outreach/templates/:id
  if (segments[0] === 'outreach') {
    if (segments.length === 1) {
      return [{ label: 'Outreach' }]
    }
    if (segments[1] === 'templates') {
      if (segments.length === 2) {
        return [
          { label: 'Outreach', href: '/app/outreach' },
          { label: 'Templates' },
        ]
      }
      if (segments.length === 3) {
        const templateLabel = overrides.templateName ?? 'Template'
        return [
          { label: 'Outreach', href: '/app/outreach' },
          { label: 'Templates', href: '/app/outreach/templates' },
          { label: templateLabel },
        ]
      }
    }
  }

  return [{ label: 'Dashboard' }]
}

function LayoutContent() {
  const location = useLocation()
  const { overrides } = useBreadcrumb()
  const breadcrumbs = getBreadcrumbs(location.pathname, overrides)

  return (
    <>
      <header className="flex h-16 shrink-0 items-center justify-between gap-2">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator
              orientation="vertical"
              className="mr-2 data-[orientation=vertical]:h-4"
            />
            <Breadcrumb>
              <BreadcrumbList>
                {breadcrumbs.map((crumb, index) => (
                  <React.Fragment key={index}>
                    <BreadcrumbItem>
                      {crumb.href ? (
                        <BreadcrumbLink asChild>
                          <Link to={crumb.href}>{crumb.label}</Link>
                        </BreadcrumbLink>
                      ) : (
                        <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                      )}
                    </BreadcrumbItem>
                    {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
                  </React.Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          <div className="flex items-center gap-2 px-4">
            <ThemeToggle />
          </div>
      </header>
      <div className="flex flex-1 flex-col gap-4 p-4 pt-0 overflow-auto">
        <Outlet />
      </div>
    </>
  )
}

export default function Layout() {
  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <BreadcrumbProvider>
          <LayoutContent />
        </BreadcrumbProvider>
      </SidebarInset>
    </SidebarProvider>
  )
}
